<template>
	<div
		v-if="active && (hasContentSlot || hasFooterSlot)"
		ref="pop"
		v-click-outside="onClickOutside"
		:class="compiledClasses"
		:style="{
			left: coordinate.x + 'px',
			top: coordinate.y + 'px',
			'--arrow-offset': arrowOffset + 'px',
			width: width ? `${width}px` : null,
			'max-width': width ? `${width}px` : null,
		}"
		class="pop"
		data-cy="pop"
	>
		<div
			v-if="title || closeable"
			class="pop__header"
		>
			<helio-icon
				v-if="state"
				:token="iconToken"
				weight="light"
				class="pop__icon"
			/>

			<h4
				v-if="title"
				class="pop__title"
			>
				{{ title }}
			</h4>

			<div
				@keyup.enter="$emit('close')"
				@keyup.space="$emit('close')"
			>
				<helio-icon
					v-if="closeable"
					class="pop__close"
					color="night-gray9"
					token="times"
					weight="light"
					:aria-label="$t('aria-label.close-button')"
					@click="$emit('close')"
				/>
			</div>
		</div>

		<div
			v-if="hasContentSlot"
			class="pop__content"
		>
			<slot name="content" />
		</div>

		<div
			v-if="hasFooterSlot"
			class="pop__footer"
		>
			<slot name="footer" />
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'Pop',

	components: {
		'helio-icon': Icon,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
		},

		anchor: {
			type: String,
			required: true,
		},

		parentPosition: {
			type: String,
			default: 'normal',
			validator: value => [
				'fixed',
				'normal',
			].indexOf(value) !== -1,
		},

		arrow: {
			type: Boolean,
			required: false,
			default: true,
		},

		closeable: {
			type: Boolean,
			required: false,
			default: false,
		},

		closeOnOutsideClick: {
			type: Boolean,
			required: false,
			default: true,
		},

		gap: {
			type: Number,
			required: true,
		},

		initialPosition: {
			type: String,
			required: true,
			validator: value => [
				'left',
				'bottom',
				'top',
				'right',
			].indexOf(value) !== -1,
		},

		initialOffset: {
			type: String,
			required: true,
			validator: value => [
				'bottom',
				'center',
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
		},

		parentClass: {
			type: String,
			required: false,
			default: '',
		},

		shadow: {
			type: String,
			required: false,
			default: 'small',
			validator: value => [
				'x-small',
				'small',
				'normal',
				'medium',
				'large',
			].indexOf(value) !== -1,
		},

		skin: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
		},

		state: {
			type: String,
			required: false,
			default: null,
			validator: value => [
				'alert',
				'danger',
				'info',
				'success',
			].indexOf(value) !== -1,
		},

		title: {
			type: String,
			required: false,
			default: null,
		},

		width: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets a custom width for the Pop',
		},
	},

	data() {
		return {
			element: null,
			coordinate: { x: 0, y: 0 },
			arrowOffset: 0,
			position: null,
			offset: null,
			visible: false,
			verticalOffsets: ['top', 'center', 'bottom'],
			horizontalOffset: ['left', 'center', 'right'],
			animating: false,
		};
	},

	computed: {
		hasContentSlot() {
			return !!this.$slots.content || !!this.$scopedSlots.content;
		},

		hasFooterSlot() {
			return !!this.$slots.footer || !!this.$scopedSlots.footer;
		},

		iconToken() {
			switch (this.state) {
				case 'danger':
					return 'times-circle';

				case 'success':
					return 'check-circle';

				case 'alert':
					return 'exclamation-triangle';

				case 'info':
				default:
					return 'info-circle';
			}
		},

		compiledClasses() {
			let compiledClasses = '';
			compiledClasses += this.visible ? ' pop--visible' : ' pop--hidden';
			compiledClasses += this.state ? ` pop--${this.state}` : ' pop--no-state';
			compiledClasses += ` pop--${this.skin}`;
			compiledClasses += ` pop--shadow-${this.shadow}`;
			compiledClasses += this.parentPosition === 'fixed' ? ' pop--fixed' : '';
			compiledClasses += ` pop--${this.position}`;
			compiledClasses += this.arrow ? ` pop--arrow-${this.inversePosition(this.position)}` : ' pop--no-arrow';
			compiledClasses += ` ${this.parentClass}`;
			compiledClasses += this.animating ? ' pop--animating' : '';
			return compiledClasses;
		},
	},

	watch: {
		active(newValue) {
			this.alignOnChanges();

			if (newValue) {
				this.$emit('open');
			} else {
				this.$emit('close');
			}

			this.animate();
		},
	},

	created() {
		this.position = this.initialPosition;

		if (
			(this.positionIsHorizontal() && this.verticalOffsets.includes(this.initialOffset))
			|| (this.positionIsVertical() && this.horizontalOffset.includes(this.initialOffset))
		) {
			this.offset = this.initialOffset;
		} else {
			this.offset = 'center';
		}
	},

	mounted() {
		window.addEventListener('resize', this.alignOnChanges);
		window.addEventListener('keyup', this.checkForEscape);
		this.alignOnChanges();
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.alignOnChanges);
		window.removeEventListener('keyup', this.checkForEscape);
	},

	methods: {
		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.active) {
				this.$emit('close');
			}
		},

		inversePosition(position) {
			switch (position) {
				case 'bottom':
					return 'top';

				case 'left':
					return 'right';

				case 'right':
					return 'left';

				case 'top':
				default:
					return 'bottom';
			}
		},

		alignOnChanges() {
			this.$nextTick(() => {
				if (this.$refs.pop) {
					this.adjustPosition();
				}
			});
		},

		adjustPosition(step = 0) {
			// We get the inverse position
			const inversePosition = this.inversePosition(this.position);

			// We get our anchor element
			this.element = document.querySelector(`[data-anchor=${this.anchor}]`);

			// If the element does not exist, don't render the pop
			if (this.element == null) {
				console.warn('Anchor element does not exist, pop is not rendered');

				this.visible = false;

				return;
			}

			// We get the top and left property of our element
			let x = this.element.getBoundingClientRect().left;
			let y = this.element.getBoundingClientRect().top;
			const { width, height } = this.element.getBoundingClientRect();
			const { clientWidth, clientHeight } = this.$refs.pop;

			// In pretty much all cases, consider the scroll
			if (this.parentPosition !== 'fixed') {
				x += window.scrollX || document.documentElement.scrollLeft || 0;
				y += window.scrollY || document.documentElement.scrollTop || 0;
			}

			// We might need to adjust the offset of the arrows
			let arrowOffset = -5;

			// We centered the pop in the on the anchor
			x += (width / 2) - (clientWidth / 2);
			y += (height / 2) - (clientHeight / 2);

			// We positioned the pop based on the props position
			switch (this.position) {
				case 'left':
					x -= (clientWidth / 2) + (width / 2) + this.gap;
					break;

				case 'right':
					x += (clientWidth / 2) + (width / 2) + this.gap;
					break;

				case 'top':
					y -= (clientHeight / 2) + (height / 2) + this.gap;
					break;

				case 'bottom':
					y += (clientHeight / 2) + (height / 2) + this.gap;
					break;

				default:
					break;
			}
			// We adjust the pop coordinates based on the offset
			if (this.positionIsHorizontal() && this.offset !== 'center') {
				let verticalOffset = (clientHeight / 2);

				if (this.arrow) {
					verticalOffset -= 12;
				}

				verticalOffset *= this.offset === 'top' ? -1 : 1;
				y += verticalOffset;
				arrowOffset -= verticalOffset;
			}

			if (this.positionIsVertical() && this.offset !== 'center') {
				let horizontalOffset = (clientWidth / 2);

				if (this.arrow) {
					horizontalOffset -= 12;
				}

				horizontalOffset *= this.offset === 'left' ? -1 : 1;
				x += horizontalOffset;
				arrowOffset -= horizontalOffset;
			}

			// We flip the position if left or right and
			// is going outside of the window
			if (this.parentPosition === 'normal' && this.positionIsHorizontal()) {
				if (this.stickOutHorizontally(x, clientWidth)) {
					if (step === 0) {
						this.position = inversePosition;
						this.adjustPosition(1);
						return;
					}

					if (step === 1) {
						this.position = 'top';
						this.adjustPosition(2);
						return;
					}
				}

				// If we stick out vertically, we adjust the pop offset
				// and the arrow Y offset in order to be sure that all
				// the information in the pop is visible
				if (this.stickOutVertically(y, clientHeight)) {
					const verticalStickoutOffset = this.getVerticalStickoutOffset(y, clientHeight);
					y += verticalStickoutOffset;
					arrowOffset -= verticalStickoutOffset;
				}
			}

			// We flip the position if top or bottom and
			// is going outside of the window
			if (this.parentPosition === 'normal' && this.positionIsVertical()) {
				if (this.stickOutVertically(y, clientHeight)) {
					if (step === 0) {
						this.position = inversePosition;
						this.adjustPosition(1);
						return;
					}

					if (step === 1) {
						this.position = 'right';
						this.adjustPosition(2);
						return;
					}
				}

				// If we stick out horizontally, we adjust the pop offset
				// and the arrow X offset in order to be sure that all
				// the information in the pop is visible
				if (this.stickOutHorizontally(x, clientWidth)) {
					const horizontalStickoutOffset = this.getHorizontalStickoutOffset(x, clientWidth);
					x += horizontalStickoutOffset;
					arrowOffset -= horizontalStickoutOffset;
				}
			}

			this.coordinate.x = x;
			this.coordinate.y = y;
			this.arrowOffset = arrowOffset;
			this.visible = true;
		},

		getVerticalStickoutOffset(y, clientHeight) {
			if (this.stickOutTheTop(y)) {
				return (Math.abs(y) + this.gap) * -1;
			}

			if (this.stickOutTheBottom(y, clientHeight)) {
				return (document.body.offsetHeight - this.gap) - (y + clientHeight);
			}

			return null;
		},

		getHorizontalStickoutOffset(x, clientWidth) {
			if (this.stickOutTheLeftSide(x)) {
				return Math.abs(x) + this.gap;
			}

			if (this.stickOutTheRightSide(x, clientWidth)) {
				return (window.innerWidth - this.gap) - (x + clientWidth);
			}

			return null;
		},

		stickOutHorizontally(x, clientWidth) {
			return this.stickOutTheRightSide(x, clientWidth) || this.stickOutTheLeftSide(x);
		},

		stickOutVertically(y, clientHeight) {
			return this.stickOutTheBottom(y, clientHeight) || this.stickOutTheTop(y);
		},

		stickOutTheRightSide(x, clientWidth) {
			return x + clientWidth > window.innerWidth - this.gap;
		},

		stickOutTheBottom(y, clientHeight) {
			return y + clientHeight > document.body.offsetHeight - this.gap;
		},

		stickOutTheLeftSide(x) {
			return x < this.gap;
		},

		stickOutTheTop(y) {
			return y < this.gap;
		},

		positionIsVertical() {
			return this.position === 'top' || this.position === 'bottom';
		},

		positionIsHorizontal() {
			return this.position === 'left' || this.position === 'right';
		},

		onClickOutside() {
			if (this.closeOnOutsideClick) {
				this.$emit('close');
			}
		},

		animate() {
			this.animating = true;

			setTimeout(() => {
				this.animating = false;
			}, 300);
		},
	},
};
</script>

<style lang="scss" src="./pop.scss" />
